/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import type { SimpleMenuItem, PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import type { ResourcesById, ProjectResource, TenantResource, RunbookResource, EnvironmentResource, RunbookProgressionResource, RunbooksDashboardItemResource, RunbookSnapshotResource, ResourceCollection, TriggerResource, TriggerScheduleResource, RunRunbookActionResource, RunbookRunTemplateResource, GitRefResource, } from "@octopusdeploy/octopus-server-client";
import { HasRunbooksInGit, Permission, TenantedDeploymentMode, TriggerActionCategory } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import { ProjectPageTitleAccessory } from "~/areas/projects/components/ProjectPageTitleAccessory";
import { NextScheduledRuns } from "~/areas/projects/components/Runbooks/NextScheduledRuns";
import RunbooksNavigationTabs from "~/areas/projects/components/Runbooks/RunbooksNavigationTabs";
import { repository } from "~/clientInstance";
import Card from "~/components/Card/Card";
import { environmentChipListIncludingMissing } from "~/components/Chips";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent";
import Logo from "~/components/Logo";
import InternalLink from "~/components/Navigation/InternalLink";
import InternalRedirect from "~/components/Navigation/InternalRedirect";
import { NoResults } from "~/components/NoResults/NoResults";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import Section from "~/components/Section";
import { timeOperation, timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import ScheduledTriggerDescriptionHelper from "~/utils/ScheduledTriggerDescriptionHelper";
import { LastPublishedChip } from "./LastPublishedChip";
import PublishButton from "./PublishButton";
import { RunNowButton } from "./RunNowButton";
import { useRunbookContext } from "./RunbookContext";
import RunbookOnboarding from "./RunbookOnboarding";
import styles from "./RunbookOverviewLayout.module.less";
import RunbookTaskStatusDetails from "./RunbookTaskStatusDetails/RunbookTaskStatusDetails";
import { isRunbookConsumerTryingToRunAnUnpublishedSnapshot } from "./isRunbookConsumerTryingToRunAnUnpublishedSnapshot";
import untenantedDeploymentLogo from "./un-tenanted-deployment-logo.svg";
interface RunbookOverviewPageState extends DataBaseComponentState {
    publishedRunbookSnapshot: RunbookSnapshotResource;
    runbookRunTemplate: RunbookRunTemplateResource;
    progression: RunbookProgressionResource;
    tenants: TenantResource[];
    environmentsById: ResourcesById<EnvironmentResource>;
    hasSteps: boolean;
    failedChecks: Array<{
        permission: Permission;
        isNotAllowed: boolean;
    }>;
    triggersResponse: ResourceCollection<TriggerResource>;
}
export interface RunbookOverviewPageRouteProps {
    runbookId: string;
}
const refreshIntervalInMs = 15000;
interface RunbookOverviewPageInternalProps extends RunbookOverviewPageProps {
    runbook: RunbookResource;
    project: ProjectResource;
    refreshGitVariableErrors: () => Promise<void>;
    gitRef: Readonly<GitRefResource> | undefined;
}
class RunbookOverviewPageInternal extends DataBaseComponent<RunbookOverviewPageInternalProps, RunbookOverviewPageState> {
    constructor(props: RunbookOverviewPageInternalProps) {
        super(props);
        this.state = {
            hasSteps: false,
            publishedRunbookSnapshot: null!,
            runbookRunTemplate: null!,
            progression: null!,
            tenants: [],
            environmentsById: null!,
            failedChecks: [],
            triggersResponse: null!,
        };
    }
    async componentDidMount() {
        if (HasRunbooksInGit(this.props.project.PersistenceSettings)) {
            // This page does not support Git Runbooks, we redirect to the list page
            // at the top of the render method.
            return;
        }
        // noinspection ES6MissingAwait This is an async method, but we don't care about the result, so fire and forget (no await).
        this.props.refreshGitVariableErrors();
        await this.doBusyTask(async () => {
            await this.reload();
            await this.startRefreshLoop(() => this.getData(), refreshIntervalInMs, true, timeOperationOptions.forRefresh());
        });
    }
    async componentDidUpdate(prevProps: RunbookOverviewPageInternalProps) {
        if (HasRunbooksInGit(this.props.project.PersistenceSettings)) {
            // This page does not support Git Runbooks, we redirect to the list page
            // at the top of the render method.
            return;
        }
        const nextRunbook = this.props.runbook;
        const currentRunbook = prevProps.runbook;
        if (!isEqual(currentRunbook, nextRunbook)) {
            await this.doBusyTask(async () => {
                return await this.reload();
            });
        }
    }
    getData = async () => {
        const project = this.props.project;
        const runbook = this.props.runbook;
        const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
        const [tenants, environmentsById] = await Promise.all([tenantsPromise, repository.Environments.allById()]);
        const requiredPermissions = [
            { permission: Permission.ProjectView, project: project.Id, tenant: "*", projectGroup: project.ProjectGroupId },
            { permission: Permission.EnvironmentView, wildcard: true },
        ];
        const failedChecks = requiredPermissions
            .map((check) => ({
            permission: check.permission,
            isNotAllowed: !isAllowed(check),
        }))
            .filter((check) => check.isNotAllowed);
        if (failedChecks.length > 0) {
            this.setState({
                failedChecks,
            });
            return null;
        }
        const runbookProcess = repository.RunbookProcess.get(runbook.RunbookProcessId);
        const runbookProgression = repository.Progression.getRunbookProgression(runbook);
        const runbookTriggers = repository.Projects.getTriggers(project, 0, 5, undefined, TriggerActionCategory.Runbook, [runbook.Id]);
        const publishedRunbookSnapshot = runbook.PublishedRunbookSnapshotId ? await repository.RunbookSnapshots.get(runbook.PublishedRunbookSnapshotId) : null!;
        const runbookRunTemplate = publishedRunbookSnapshot ? await repository.RunbookSnapshots.getRunbookRunTemplate(publishedRunbookSnapshot) : null!;
        return {
            progression: await runbookProgression,
            tenants,
            environmentsById,
            hasSteps: (await runbookProcess).Steps.length > 0,
            publishedRunbookSnapshot,
            runbookRunTemplate,
            triggersResponse: await runbookTriggers,
        };
    };
    async reload() {
        this.setState(await timeOperation(timeOperationOptions.forInitialLoad(), () => this.getData()));
    }
    renderDashboard() {
        if (!this.state.progression || Object.keys(this.state.progression.RunbookRuns).length === 0) {
            const runbook = this.props.runbook;
            return (<>
                    <div className={styles.emptyCell}>
                        There are no runs for this Runbook yet.&nbsp;<InternalLink to={links.runbookRunNowPage.generateUrl({ spaceId: this.props.spaceId, projectSlug: this.props.projectSlug, runbookId: runbook.Id })}>Run Now</InternalLink>
                    </div>
                    <NoResults />
                </>);
        }
        const progression = this.state.progression;
        const environments = progression.Environments;
        const environmentKeys = Object.keys(progression.RunbookRuns);
        const rowsByTenant: Record<string, RunbooksDashboardItemResource[]> = {};
        environmentKeys.map((i) => {
            const runbookRuns = progression.RunbookRuns[i];
            runbookRuns.forEach((r) => {
                const untenantedRowKey = "";
                const tenant = r.TenantId ? this.state.tenants!.find((t) => t.Id === r.TenantId) : undefined;
                if (r.TenantId && tenant === undefined) {
                    return;
                }
                const rowKey = tenant ? tenant.Name : untenantedRowKey;
                if (!rowsByTenant[rowKey]) {
                    rowsByTenant[rowKey] = [];
                }
                rowsByTenant[rowKey].push(r);
            });
        });
        const rowsBySortedTenantKeys = Object.keys(rowsByTenant).sort((a, b) => {
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
        const isTenanted = this.props.runbook.MultiTenancyMode !== TenantedDeploymentMode.Untenanted;
        return (<div>
                <div className={styles.dashboardCards}>
                    {rowsBySortedTenantKeys.map((tenantName, tenantRowIndex) => {
                const tenant = this.state.tenants.find((t) => t.Name === tenantName);
                const tenantId = tenant ? tenant.Id : null;
                return (<div className={styles.bodyRow} key={tenantRowIndex}>
                                {isTenanted && (<Section className={cn(styles.tenantName, styles.groupHeader)} key={tenantRowIndex}>
                                        {tenant && this.tile(tenant.Links.Logo, tenant.Name, links.tenantOverviewPage.generateUrl({ spaceId: tenant.SpaceId, tenantId: tenant.Id }))}
                                        {!tenant && this.tile(untenantedDeploymentLogo, "Untenanted")}
                                    </Section>)}
                                {environmentKeys.map((x, environmentRowIndex) => {
                        const runbookRuns = progression.RunbookRuns[x];
                        const environment = environments.find((e) => e.Id === x);
                        const thisRunbookRun = runbookRuns.find((o) => o.TenantId === tenantId);
                        if (!environment || !thisRunbookRun) {
                            return null;
                        }
                        else {
                            const menuItems = [];
                            if (!isRunbookConsumerTryingToRunAnUnpublishedSnapshot(this.props.project, this.props.runbook, thisRunbookRun.RunbookSnapshotId)) {
                                menuItems.push(OverflowMenuItems.navItem("Run on...", links.runbookRunSnapshotNowPage.generateUrl({
                                    spaceId: this.props.spaceId,
                                    projectSlug: this.props.projectSlug,
                                    runbookId: this.props.runbookId,
                                    runbookSnapshotId: thisRunbookRun.RunbookSnapshotId,
                                }), {
                                    permission: Permission.RunbookRunCreate,
                                    project: this.props.project.Id,
                                    wildcard: true,
                                }));
                            }
                            menuItems.push(OverflowMenuItems.navItem("View snapshot", links.projectRunbookSnapshotInfoPage.generateUrl({
                                spaceId: this.props.project.SpaceId,
                                projectSlug: this.props.project.Slug,
                                runbookId: this.props.runbookId,
                                runbookSnapshotId: thisRunbookRun.RunbookSnapshotId,
                            }), {
                                permission: Permission.RunbookView,
                                project: this.props.project.Id,
                                wildcard: true,
                            }));
                            return (<Card key={environmentRowIndex} className={styles.cardContainer} contentClassName={styles.cardContentContainer} leftAlign={true} logo={null} header={<div className={styles.runTaskEnvironment}>{environment.Name}</div>} content={<div>
                                                        <div className={styles.runTaskDetails}>
                                                            <RunbookTaskStatusDetails project={this.props.project} item={thisRunbookRun}/>
                                                            <div className={styles.runActions}>
                                                                <OverflowMenu menuItems={menuItems} tabIndex={-1}/>
                                                            </div>
                                                        </div>
                                                        {thisRunbookRun.RunbookSnapshotNotes ? <div className={styles.runTaskNotes}>{thisRunbookRun.RunbookSnapshotNotes}</div> : null}
                                                    </div>}/>);
                        }
                    })}
                            </div>);
            })}
                </div>
            </div>);
    }
    tile(logoUrl: string, name: string, toUrl?: LinkHref) {
        if (toUrl) {
            return (<InternalLink to={toUrl} className={styles.rowHeader}>
                    <span>
                        <Logo url={logoUrl} size="2.25rem"/>
                    </span>
                    <span className={styles.tileName}>{name}</span>
                </InternalLink>);
        }
        else {
            return (<div className={styles.rowHeader}>
                    <span>
                        <Logo url={logoUrl} size="2.25rem"/>
                    </span>
                    <span className={styles.tileName}>{name}</span>
                </div>);
        }
    }
    render() {
        const runbook = this.props.runbook;
        const project = this.props.project;
        if (HasRunbooksInGit(project.PersistenceSettings)) {
            // This page does not support Git Runbooks, go straight to the run list page.
            return <InternalRedirect to={links.projectRunbookRunsListPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: runbook.Id })}/>;
        }
        const breadcrumbTitle = "Runbooks";
        const breadcrumbPath = links.projectRunbooksPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug });
        if (this.state.failedChecks.length > 0) {
            return (<PageContent header={{ title: runbook.Name, breadcrumbs: [{ label: breadcrumbTitle, pageUrl: breadcrumbPath }], contextSelector: <ProjectPageTitleAccessory /> }} busy={this.state.busy} errors={this.errors} callout={{
                    type: "information",
                    title: "Permission required",
                    content: `The ${this.state.failedChecks[0].permission} permission is required to view project overview details`,
                }}>
                    <RunbooksNavigationTabs />
                </PageContent>);
        }
        const nextScheduledElement = this.renderNextScheduled();
        if (!this.state.hasSteps) {
            const defineRunbookProcessPageAction: PrimaryPageAction = {
                type: "navigate",
                label: "Define your Runbook Process",
                path: links.projectRunbookProcessListPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: runbook.Id, processId: runbook.RunbookProcessId }),
                hasPermissions: isAllowed({ permission: Permission.RunbookEdit, project: project.Id, wildcard: true }),
            };
            return (<PageContent header={{
                    title: runbook.Name,
                    breadcrumbs: [{ label: breadcrumbTitle, pageUrl: breadcrumbPath }],
                    primaryAction: defineRunbookProcessPageAction,
                    contextSelector: <ProjectPageTitleAccessory />,
                }} busy={this.state.busy} errors={this.errors} sidebar={nextScheduledElement}>
                    <RunbooksNavigationTabs />
                    <RunbookOnboarding />
                </PageContent>);
        }
        const runbookRunTemplate = this.state.runbookRunTemplate;
        const publishedRunbookSnapshot = this.state.publishedRunbookSnapshot;
        const isRunbookRunTemplateModified = runbookRunTemplate && (runbookRunTemplate.IsRunbookProcessModified || runbookRunTemplate.IsVariableSetModified || runbookRunTemplate.IsLibraryVariableSetModified);
        const pageActions: PageAction[] = [];
        if (this.state.hasSteps) {
            pageActions.push({
                type: "custom",
                key: "Publish",
                content: <PublishButton publishedRunbookSnapshot={publishedRunbookSnapshot} isRunbookRunTemplateModified={isRunbookRunTemplateModified}/>,
                hasPermissions: isAllowed({
                    permission: Permission.RunbookEdit,
                    project: this.props.project.Id,
                    wildcard: true,
                }),
            });
        }
        pageActions.push({
            type: "custom",
            key: "Run now",
            content: <RunNowButton spaceId={this.props.spaceId} isDisabled={!this.state.hasSteps} projectSlug={project.Slug} projectId={project.Id} runbookId={runbook.Id}/>,
            hasPermissions: isAllowed({
                permission: Permission.RunbookRunCreate,
                project: this.props.project.Id,
                wildcard: true,
            }),
        });
        const overflowActions: SimpleMenuItem[] = [];
        const isAllowedToViewRunbook = isAllowed({
            permission: Permission.RunbookView,
            project: project.Id,
            projectGroup: project.ProjectGroupId,
            wildcard: true,
        });
        const isAllowedToViewAudit = isAllowed({
            permission: Permission.EventView,
            wildcard: true,
        });
        if (isAllowedToViewRunbook) {
            overflowActions.push({
                type: "internal-link",
                label: "View Snapshots",
                path: links.projectRunbookSnapshotsPage.generateUrl({ spaceId: runbook.SpaceId, projectSlug: project.Slug, runbookId: runbook.Id }),
            });
        }
        if (isAllowedToViewAudit) {
            overflowActions.push({
                type: "internal-link",
                label: "Audit Trail",
                path: links.auditPage.generateUrl({ projects: [project.Id], documentTypes: ["Runbooks"], regardingAny: [runbook.Id] }),
            });
        }
        const titleChip = publishedRunbookSnapshot && <LastPublishedChip project={project} publishedRunbookSnapshot={publishedRunbookSnapshot} isRunbookRunTemplateModified={isRunbookRunTemplateModified}/>;
        return (<PageContent header={{
                title: runbook.Name,
                chip: titleChip,
                breadcrumbs: [{ label: breadcrumbTitle, pageUrl: breadcrumbPath }],
                pageActions: pageActions,
                overflowActions: overflowActions,
                contextSelector: <ProjectPageTitleAccessory />,
            }} sidebar={nextScheduledElement} busy={this.state.busy} errors={this.errors}>
                <RunbooksNavigationTabs />
                {this.renderDashboard()}
            </PageContent>);
    }
    private renderNextScheduled() {
        const project = this.props.project;
        const runbook = this.props.runbook;
        if (!this.state.triggersResponse) {
            return null;
        }
        return (<NextScheduledRuns project={project} runbook={runbook} triggersResponse={this.state.triggersResponse} triggerActionCategory={TriggerActionCategory.Runbook} renderBuildTriggerRow={(renderProps) => {
                const trigger = renderProps.trigger;
                const description = this.getTriggerDescription(trigger);
                return (<div className={styles.nextScheduledRow}>
                            <div>
                                <InternalLink to={links.projectRunbookEditScheduledTriggerPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, triggerId: trigger.Id })}>{trigger.Name}</InternalLink>
                            </div>
                            <div>{description}</div>
                        </div>);
            }}/>);
    }
    private getTriggerDescription(trigger: TriggerResource) {
        const scheduleDescription = ScheduledTriggerDescriptionHelper.getScheduleDescription(trigger.Filter as TriggerScheduleResource);
        const destinationEnvironmentIds = (trigger.Action as RunRunbookActionResource).EnvironmentIds;
        const environments = Object.values(this.state.environmentsById);
        const environmentChips = environmentChipListIncludingMissing(environments, destinationEnvironmentIds);
        return (<span>
                Run {scheduleDescription} on {environmentChips}.
            </span>);
    }
    static displayName = "RunbookOverviewPageInternal";
}
interface RunbookOverviewPageProps {
    project: ProjectResource;
    refreshGitVariableErrors: () => Promise<void>;
    spaceId: string;
    projectSlug: string;
    runbookId: string;
    gitRef: Readonly<GitRefResource> | undefined;
}
function RunbookOverviewPage(props: RunbookOverviewPageProps) {
    const runbookContext = useRunbookContext();
    const runbook = runbookContext.state.runbook;
    if (!runbook) {
        return (<PageContent header={{ title: "", breadcrumbs: [{ label: "Runbooks", pageUrl: links.projectRunbooksPage.generateUrl({ spaceId: props.project.SpaceId, projectSlug: props.project.Slug }) }] }} busy={Promise.resolve()}>
                <RunbooksNavigationTabs />
            </PageContent>);
    }
    return <RunbookOverviewPageInternal project={props.project} runbook={runbook} refreshGitVariableErrors={props.refreshGitVariableErrors} gitRef={props.gitRef} runbookId={props.runbookId} spaceId={props.spaceId} projectSlug={props.projectSlug}/>;
}
export default RunbookOverviewPage;
